<template>
  <div>
    <a-table
      :columns="columns"
      :data-source="tableData"
      :scroll="{ x: true }"
      :loading="fetching"
      :pagination="false"
    >
      <div slot="offersAllInFileTitleRenderer">
        Offers ID
        <div>{{ $t("synchronization.all_in_file") }}</div>
      </div>

      <div slot="offersSellsTitleRenderer">
        Offers ID
        <div class="lowercase">{{ $t("synchronization.sells") }}</div>
      </div>

      <div slot="offersNotSellsTitleRenderer">
        Offers ID
        <div class="lowercase">{{ $t("synchronization.not_sells") }}</div>
      </div>

      <div slot="offersXmlErrorsTitleRenderer">
        Offers ID
        <div class="lowercase">{{ $t("synchronization.xml_errors") }}</div>
      </div>

      <div slot="offersNeedActionTitleRenderer">
        Offers ID
        <div class="lowercase">{{ $t("synchronization.need_action") }}</div>
      </div>

      <DateRenderer
        slot="last_sync_ends_at"
        slot-scope="date"
        :date="date || new Date().toISOString()"
      />

      <template
        slot="nextSyncAtRenderer"
        slot-scope="nextSyncAt, record"
      >
        <template v-if="record.task && ['STARTED', 'PROGRESS'].includes(record.task.status)">
          {{ $t("synchronization.status_progress") }}
        </template>
        <template v-else-if="record.task && record.task.status === 'SUCCESS'">
          {{ $t("synchronization.status_success") }}
        </template>
        <template v-else-if="record.task && ['IGNORED', 'FAILURE'].includes(record.task.status)">
          {{ $t("synchronization.status_failure") }}
        </template>
        <template v-else-if="record.task && record.task.status === 'STOPPED'">
          {{ $t("synchronization.status_stopped") }}
        </template>
        <template v-else-if="record.task && record.task.status === 'PENDING'">
          <span>{{ $t("synchronization.approximately_through") }}</span>
          <ReverseTimer :seconds="nextSyncAt" />
        </template>
      </template>

      <div
        slot="urlRenderer"
        slot-scope="url, record"
        class="file-url"
      >
        <div>XML File ({{ record.format }})</div>
        <a
          :href="url"
          target="_blank"
        >
          {{ url }}
        </a>
      </div>

      <template
        slot="offersAllInFileRenderer"
        slot-scope="count, record"
      >
        <a-spin v-if="synchronizationsDraftsMetaFetching">
          <a-icon
            slot="indicator"
            type="loading"
            style="font-size: 24px"
            spin
          />
        </a-spin>
        <span v-else-if="record.draftMeta">{{ record.draftMeta.offersAllCount }}</span>
      </template>

      <template
        slot="offersSellsRenderer"
        slot-scope="count, record"
      >
        <a-spin v-if="synchronizationsDraftsMetaFetching">
          <a-icon
            slot="indicator"
            type="loading"
            style="font-size: 24px"
            spin
          />
        </a-spin>
        <span v-else-if="record.draftMeta">{{ record.draftMeta.offersIsSellingCount }}</span>
      </template>

      <template
        slot="offersNotSellsRenderer"
        slot-scope="count, record"
      >
        <a-spin v-if="synchronizationsDraftsMetaFetching">
          <a-icon
            slot="indicator"
            type="loading"
            style="font-size: 24px"
            spin
          />
        </a-spin>
        <span v-else-if="record.draftMeta">{{ record.draftMeta.offersNotSellingCount }}</span>
      </template>

      <template
        slot="offersXmlErrorsRenderer"
        slot-scope="count, record"
      >
        <a-spin v-if="synchronizationsDraftsMetaFetching">
          <a-icon
            slot="indicator"
            type="loading"
            style="font-size: 24px"
            spin
          />
        </a-spin>
        <span v-else-if="record.draftMeta">{{ record.draftMeta.offersWithErrorsCount }}</span>
      </template>

      <template
        slot="offersNeedActionRenderer"
        slot-scope="count, record"
      >
        <a-spin v-if="synchronizationsDraftsMetaFetching">
          <a-icon
            slot="indicator"
            type="loading"
            style="font-size: 24px"
            spin
          />
        </a-spin>
        <span v-else-if="record.draftMeta">{{ record.draftMeta.offersIsNeedActionCount }}</span>
      </template>

      <template
        slot="isActiveRenderer"
        slot-scope="isActive"
      >
        <div :class="isActive === true ? 'sync-active' : 'sync-paused'">
          {{
            isActive === true
              ? $t(`synchronization.sync_active`)
              : $t(`synchronization.sync_paused`)
          }}
        </div>
      </template>

      <a-row
        :gutter="8"
        slot="actionsRenderer"
        slot-scope="text, record"
      >
        <a-col span="12">
          <router-link
            :to="{
              name: 'sync-drafts',
              params: { uuid: record.uuid },
              query: { url: record.url }
            }"
          >
            <a-button
              class="sync-button-primary"
              type="dashed"
              shape="circle"
              icon="arrow-right"
            />
          </router-link>
        </a-col>
        <a-col span="12">
          <a-dropdown :trigger="['click']">
            <a-button
              :loading="startSyncRequestUuid === record.uuid"
              type="dashed"
              shape="circle"
              icon="setting"
            />
            <a-menu slot="overlay">
              <a-menu-item key="1">
                <router-link :to="{ name: 'sync-settings', params: { uuid: record.uuid } }">
                  {{ $t("synchronization.edit_synchronization") }}
                </router-link>
              </a-menu-item>
              <a-menu-item
                v-if="record.is_active"
                key="stop_sync"
                @click="handleOpenStopModal(record.uuid)"
              >
                {{ $t("synchronization.stop") }}
              </a-menu-item>
              <a-menu-item
                v-else
                key="start_sync"
                @click="handleStartSync(record.uuid, updateSyncCallback)"
              >
                {{ $t("synchronization.renew") }}
              </a-menu-item>
              <a-menu-item
                key="3"
                @click="handleOpenDeleteModal(record.uuid)"
              >
                {{ $t("synchronization.delete") }}
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </a-col>
      </a-row>
    </a-table>

    <DeleteSyncModal
      :is-opened="isDeleteModalOpened"
      :sync-uuid="currentSyncUuid"
      @close="handleCloseDeleteModal"
      @delete="updateSyncCallback"
    />

    <StopSyncModal
      :is-opened="isStopModalOpened"
      :sync-uuid="currentSyncUuid"
      @close="handleCloseStopModal"
      @stop="updateSyncCallback"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia"
import { computed, getCurrentInstance } from "vue"

import ReverseTimer from "@/components/Common/ReverseTimer"
import DateRenderer from "@/components/TableRenderer/DateRenderer.vue"
import i18n from "@/i18n"
import { SyncDraftMeta, Synchronization, SyncTaskData } from "@/types/sync.type"

import { useSyncListStore } from "../store/useSyncListStore"
import { useSyncSettings } from "../syncList/useSyncSettings"

import DeleteSyncModal from "./DeleteSyncModal.vue"
import StopSyncModal from "./StopSyncModal.vue"

// Fix: Define dummy variables to hint types for Volar in legacy slot usage
// This is required because Volar cannot infer slot-scope types in usage of AntDV 1.x with script setup
const record = {} as Synchronization & { task?: SyncTaskData; draftMeta?: SyncDraftMeta }
const nextSyncAt = 0 as number
const date = "" as string | null
const url = "" as string
const isActive = false as boolean
// End Fix

// Helper Types/Variables
const proxy = getCurrentInstance()?.proxy as any
// End Helper Types/Variables

// Store & Route
const syncListStore = useSyncListStore()

const { fetching, synchronizations, synchronizationsDraftsMetaFetching } =
  storeToRefs(syncListStore)
// End Store & Route

// Composables
const {
  isDeleteModalOpened,
  isStopModalOpened,
  startSyncRequestUuid,
  currentSyncUuid,
  handleOpenStopModal,
  handleOpenDeleteModal,
  handleCloseStopModal,
  handleCloseDeleteModal,
  handleStartSync
} = useSyncSettings()
// End Composables

// Computed
const tableData = computed<
  (Synchronization & { task?: SyncTaskData; draftMeta?: SyncDraftMeta })[]
>(() => {
  return synchronizations.value.map((sync) => {
    return {
      ...sync,
      task: syncListStore.synchronizationsTask[sync.uuid],
      draftMeta: syncListStore.synchronizationsDraftsMeta[sync.uuid]
    }
  })
})
const columns = computed(() => [
  {
    fixed: "left",
    title: "№",
    width: 30,
    customRender: (text: any, record: any, index: number) => {
      return {
        children: index + 1,
        props: {
          style: {
            textAlign: "center"
          }
        }
      }
    }
  },
  {
    fixed: "left",
    title: i18n.t("synchronization.last_synchronization_short"),
    dataIndex: "last_sync_ends_at",
    scopedSlots: { customRender: "last_sync_ends_at" },
    width: 100
  },
  {
    fixed: "left",
    title: i18n.t("synchronization.next_synchronization_time_short"),
    dataIndex: "next_sync_at",
    scopedSlots: { customRender: "nextSyncAtRenderer" },
    width: 110
  },
  {
    fixed: "left",
    title: i18n.t("synchronization.file_url"),
    dataIndex: "url",
    scopedSlots: { customRender: "urlRenderer" }
  },
  {
    dataIndex: "offers_all_in_file",
    scopedSlots: {
      title: "offersAllInFileTitleRenderer",
      customRender: "offersAllInFileRenderer"
    },
    align: "center",
    width: 130
  },
  {
    dataIndex: "offers_are_sold",
    scopedSlots: {
      title: "offersSellsTitleRenderer",
      customRender: "offersSellsRenderer"
    },
    align: "center",
    width: 120
  },
  {
    dataIndex: "offers_not_for_sale",
    scopedSlots: {
      title: "offersNotSellsTitleRenderer",
      customRender: "offersNotSellsRenderer"
    },
    align: "center",
    width: 130
  },
  {
    dataIndex: "offers_xml_errors",
    scopedSlots: {
      title: "offersXmlErrorsTitleRenderer",
      customRender: "offersXmlErrorsRenderer"
    },
    align: "center",
    width: 120
  },
  {
    dataIndex: "offers_need_action",
    scopedSlots: {
      title: "offersNeedActionTitleRenderer",
      customRender: "offersNeedActionRenderer"
    },
    align: "center",
    width: 120
  },
  {
    title: proxy.$t("common.status"),
    dataIndex: "is_active",
    scopedSlots: { customRender: "isActiveRenderer" },
    width: 120
  },
  {
    fixed: "right",
    dataIndex: "actions",
    scopedSlots: { customRender: "actionsRenderer" },
    width: 100
  }
])
// End Computed

// Methods
const updateSyncCallback = () => {
  syncListStore.getSynchronizations()
}
// End Methods
</script>

<style lang="scss" scoped>
.file-url {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 400px;
}

.sync-active {
  color: #52c41a;
}

.sync-paused {
  color: rgba(#00000073, 0.45);
}

.sync-button-primary {
  border-color: $brand-color-6;
  color: $brand-color-6;
}
</style>
